import {
  isSignupProductKey,
  type SignupProductKey,
} from '../config/SignupProductConfig';
import { getAngularService } from './Utilities';

const API_SERVER = process.env.API_SERVER || '';
const APPLICATION_SIGNUP_SOURCE = 'application';

export type SignupContextSelection =
  | { type: 'school'; schoolId: string }
  | { type: 'school_not_listed'; stateCode: string }
  | { type: 'token'; token: string }
  | { type: 'eps_district_admin'; productKey: 'wordly_wise' | 'ispire' | 'spire' };

export interface SignupContextRequest {
  subject?: string;
  selection: SignupContextSelection;
  classLinkState?: string;
  cleverState?: string;
  canvasState?: string;
  schoologyState?: string;
}

export interface ProviderSignupContext {
  mappingStatus: 'exact' | 'missing' | 'ambiguous';
  school?: {
    id: string;
    name: string;
    address?: string;
    city?: string;
    state?: string;
    zip?: string;
  };
}

interface PreparedSignupState {
  code: string;
  label: string;
}

export type PreparedSignupContext =
  | {
    selectionMode: 'school' | 'school_not_listed' | 'token';
    schoolName: string;
    viewMode?: 'standard' | 'product';
    productKey?: SignupProductKey;
    planName?: string;
    state?: PreparedSignupState;
  }
  | {
    selectionMode: 'eps_district_admin';
    viewMode: 'district_admin';
    productKey: SignupProductKey;
    schoolName?: never;
    planName?: never;
    state?: never;
  };

interface SignupErrorResponse {
  error?: {
    code?: string;
  };
}

interface GoogleSignupUrlResponse {
  url?: string;
}

function isOptionalString(value: unknown) {
  return value === undefined || typeof value === 'string';
}

function isProviderSignupContext(value: unknown): value is ProviderSignupContext {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const context = value as Record<string, unknown>;
  const hasMappingStatus = context.mappingStatus === 'exact' ||
    context.mappingStatus === 'missing' ||
    context.mappingStatus === 'ambiguous';
  if (!hasMappingStatus) {
    return false;
  }
  if (context.mappingStatus !== 'exact') {
    return context.school === undefined;
  }
  if (!context.school || typeof context.school !== 'object') {
    return false;
  }

  const school = context.school as Record<string, unknown>;
  return typeof school.id === 'string' &&
    typeof school.name === 'string' &&
    isOptionalString(school.address) &&
    isOptionalString(school.city) &&
    isOptionalString(school.state) &&
    isOptionalString(school.zip);
}

interface ExistingAccountLoginResult {
  error?: unknown;
}

interface ExistingAccountLoginService {
  login: (
    emailAddress: string,
    password: string,
    signupSource: string,
  ) => PromiseLike<ExistingAccountLoginResult>;
}

export class SignupServiceError extends Error {
  code: string;
  status?: number;

  constructor(code: string, status?: number) {
    super(code);
    this.name = 'SignupServiceError';
    this.code = code;
    this.status = status;
  }
}

function isPreparedSignupState(value: unknown): value is PreparedSignupState {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const state = value as Record<string, unknown>;
  return typeof state.code === 'string' && typeof state.label === 'string';
}

function isPreparedSignupContext(value: unknown): value is PreparedSignupContext {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const context = value as Record<string, unknown>;
  if (context.selectionMode === 'eps_district_admin') {
    return context.viewMode === 'district_admin' &&
      isSignupProductKey(context.productKey) &&
      context.schoolName === undefined &&
      context.planName === undefined &&
      context.state === undefined;
  }

  const hasSchoolSelection = context.selectionMode === 'school' ||
    context.selectionMode === 'school_not_listed' ||
    context.selectionMode === 'token';
  const hasSchoolName = typeof context.schoolName === 'string' && context.schoolName.trim() !== '';
  const hasValidViewMode = context.viewMode === undefined ||
    context.viewMode === 'standard' ||
    context.viewMode === 'product';
  const hasValidProductKey = context.productKey === undefined ||
    isSignupProductKey(context.productKey);
  const hasValidPlanName = context.planName === undefined || typeof context.planName === 'string';
  const hasValidState = context.state === undefined || isPreparedSignupState(context.state);

  return hasSchoolSelection &&
    hasSchoolName &&
    hasValidViewMode &&
    hasValidProductKey &&
    hasValidPlanName &&
    hasValidState;
}

export async function prepareSignupContext(
  request: SignupContextRequest,
): Promise<PreparedSignupContext> {
  const response = await fetch(`${API_SERVER}/signup/context`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });
  const result = await response.json() as unknown;

  if (!response.ok) {
    const errorResult = result && typeof result === 'object'
      ? result as SignupErrorResponse
      : {};
    throw new SignupServiceError(
      errorResult.error?.code || 'signup_context_unavailable',
      response.status,
    );
  }

  if (!isPreparedSignupContext(result) || result.selectionMode !== request.selection.type) {
    throw new SignupServiceError('signup_context_unavailable');
  }

  if (request.selection.type === 'eps_district_admin' &&
    result.selectionMode === 'eps_district_admin' &&
    result.productKey !== request.selection.productKey) {
    throw new SignupServiceError('signup_context_unavailable');
  }

  return result;
}

export async function getGoogleSignupUrl(handoffId?: string): Promise<string> {
  const query = new URLSearchParams({
    app_url: window.location.origin,
    redirect_error: `${window.location.origin}/signup/error`,
    signup_source: APPLICATION_SIGNUP_SOURCE,
  });
  if (handoffId) {
    query.set('signup_handoff_id', handoffId);
  }
  const response = await fetch(`${API_SERVER}/auth/google/url/signup?${query.toString()}`, {
    method: 'GET',
    credentials: 'include',
  });
  const result = await response.json() as GoogleSignupUrlResponse;

  if (!response.ok || !result.url) {
    throw new SignupServiceError('signup_google_unavailable');
  }

  return result.url;
}

export async function startGoogleSignup(handoffId?: string): Promise<void> {
  const url = await getGoogleSignupUrl(handoffId);
  window.location.assign(url);
}

async function getProviderSignupContext(
  provider: 'canvas' | 'classlink' | 'clever' | 'schoology',
  state: string,
): Promise<ProviderSignupContext> {
  const response = await fetch(`${API_SERVER}/signup/${provider}/context`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({state}),
  });
  const result = await response.json().catch(() => null) as unknown;

  if (!response.ok) {
    const errorResult = result && typeof result === 'object'
      ? result as SignupErrorResponse
      : {};
    throw new SignupServiceError(
      errorResult.error?.code || `${provider}_continuation_unavailable`,
      response.status,
    );
  }

  if (!isProviderSignupContext(result)) {
    throw new SignupServiceError(`${provider}_continuation_unavailable`);
  }
  return result;
}

export function getClassLinkSignupContext(state: string): Promise<ProviderSignupContext> {
  return getProviderSignupContext('classlink', state);
}

export function getCleverSignupContext(state: string): Promise<ProviderSignupContext> {
  return getProviderSignupContext('clever', state);
}

export function getCanvasSignupContext(state: string): Promise<ProviderSignupContext> {
  return getProviderSignupContext('canvas', state).then((context) => {
    if (context.mappingStatus !== 'missing') {
      throw new SignupServiceError('canvas_continuation_unavailable');
    }
    return context;
  });
}

export function getSchoologySignupContext(state: string): Promise<ProviderSignupContext> {
  return getProviderSignupContext('schoology', state).then((context) => {
    if (context.mappingStatus !== 'missing') {
      throw new SignupServiceError('schoology_continuation_unavailable');
    }
    return context;
  });
}

export function startClassLinkSignup(): void {
  const query = new URLSearchParams({
    signup_source: APPLICATION_SIGNUP_SOURCE,
    app_url: window.location.origin,
  });
  window.location.assign(`${API_SERVER}/auth/classlink/login?${query.toString()}`);
}

export function startCleverSignup(): void {
  const query = new URLSearchParams({
    signup_source: APPLICATION_SIGNUP_SOURCE,
    app_url: window.location.origin,
  });
  window.location.assign(`${API_SERVER}/auth/clever/login?${query.toString()}`);
}

export function completeCleverSignup(state: string): void {
  const query = new URLSearchParams({
    clever_state: state,
    signup_source: APPLICATION_SIGNUP_SOURCE,
    app_url: window.location.origin,
  });
  window.location.assign(`${API_SERVER}/users/websignup/redirect?${query.toString()}`);
}

export function completeClassLinkSignup(state: string): void {
  const query = new URLSearchParams({
    classlink_state: state,
    signup_source: APPLICATION_SIGNUP_SOURCE,
    app_url: window.location.origin,
  });
  window.location.assign(`${API_SERVER}/users/websignup/redirect?${query.toString()}`);
}

export function completeSchoologySignup(state: string): void {
  const query = new URLSearchParams({
    schoology_state: state,
    signup_source: APPLICATION_SIGNUP_SOURCE,
    app_url: window.location.origin,
  });
  window.location.assign(`${API_SERVER}/users/websignup/redirect?${query.toString()}`);
}

export function completeCanvasSignup(state: string): void {
  const query = new URLSearchParams({
    canvas_state: state,
    signup_source: APPLICATION_SIGNUP_SOURCE,
    app_url: window.location.origin,
  });
  window.location.assign(`${API_SERVER}/users/websignup/redirect?${query.toString()}`);
}

export async function signInExistingAccount(
  emailAddress: string,
  password: string,
): Promise<void> {
  const loginService = getAngularService<ExistingAccountLoginService>('loginService');
  if (!loginService) {
    throw new SignupServiceError('signup_login_unavailable');
  }

  const result = await loginService.login(emailAddress, password, APPLICATION_SIGNUP_SOURCE);
  if (result?.error) {
    throw new SignupServiceError('signup_login_failed');
  }
}

function submitForm(action: string, fields: Record<string, string>) {
  const form = document.createElement('form');
  form.method = 'POST';
  form.action = action;

  Object.entries(fields).forEach(([name, value]) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
  });

  document.body.appendChild(form);
  form.submit();
  form.remove();
}

export function submitEmailSignup(emailAddress: string, handoffId?: string) {
  submitForm(`${API_SERVER}/users/websignup`, {
    email: emailAddress,
    signup_source: APPLICATION_SIGNUP_SOURCE,
    app_url: window.location.origin,
    ...(handoffId ? {signup_handoff_id: handoffId} : {}),
  });
}

export function completeEmailSignup(
  hash: string,
  firstName: string,
  lastName: string,
  password: string,
) {
  submitForm(`${API_SERVER}/users/${encodeURIComponent(hash)}/webupdate`, {
    first_name: firstName,
    last_name: lastName,
    password,
    signup_source: APPLICATION_SIGNUP_SOURCE,
    app_url: window.location.origin,
  });
}

const SignupService = {
  completeClassLinkSignup,
  completeCanvasSignup,
  completeCleverSignup,
  completeSchoologySignup,
  completeEmailSignup,
  getClassLinkSignupContext,
  getCanvasSignupContext,
  getCleverSignupContext,
  getSchoologySignupContext,
  getGoogleSignupUrl,
  prepareSignupContext,
  signInExistingAccount,
  startClassLinkSignup,
  startGoogleSignup,
  startCleverSignup,
  submitEmailSignup,
};

export default SignupService;
